// ===================== Bundle Picker Progress Bar =====================
// Visual-only: reads #bundle-progress-bar + .bundle-picker__progress-item thresholds,
// exposes window.updateProgressBar(quantity). Called from pdp-main-bundle-builder.js
// (getThresholdDiscount reads data-reached / data-discount on thresholds).
// ATC / first-order gift: handled via onAddToCartSuccess in custom_sitewide.js from bundle builder.

(function () {
  'use strict';

  let progressBarElement = null;
  let progressFill = null;
  let progressTrack = null;
  let progressContainer = null;
  let thresholds = [];
  let trackReady = false;

  function initProgressBar() {
    progressBarElement = document.getElementById('bundle-progress-bar');
    if (!progressBarElement) return;

    progressFill = progressBarElement.querySelector('.bundle-picker__progress-fill');
    progressTrack = progressBarElement.querySelector('.bundle-picker__progress-track');
    progressContainer = progressBarElement.querySelector('.bundle-picker__progress-container');

    const progressItems = progressBarElement.querySelectorAll('.bundle-picker__progress-item');
    thresholds = Array.from(progressItems)
      .map((item) => {
        const circle = item.querySelector('.bundle-picker__progress-circle');
        return {
          element: item,
          circle,
          value: parseInt(item.dataset.threshold, 10) || 0,
          index: parseInt(item.dataset.index, 10) || 0,
        };
      })
      .sort((a, b) => a.value - b.value);

    updateTrackPosition();

    // Avoid recursion when there are 0 thresholds (wrong/empty markup).
    if (thresholds.length > 0) updateProgressBar(0);
  }

  function updateTrackPosition() {
    if (thresholds.length === 0 || !progressBarElement) return;
    if (!progressContainer || !progressTrack) return;

    const firstItem = thresholds[0].element;
    const lastItem = thresholds[thresholds.length - 1].element;
    const firstCircle = thresholds[0].circle || firstItem.querySelector('.bundle-picker__progress-circle');
    const lastCircle = thresholds[thresholds.length - 1].circle || lastItem.querySelector('.bundle-picker__progress-circle');

    if (!firstCircle || !lastCircle) return;

    const containerRect = progressContainer.getBoundingClientRect();
    const firstCircleRect = firstCircle.getBoundingClientRect();
    const lastCircleRect = lastCircle.getBoundingClientRect();

    const firstCircleRadius = firstCircleRect.width / 2;
    const lastCircleRadius = lastCircleRect.width / 2;

    const firstCircleCenterY = firstCircleRect.top + firstCircleRadius - containerRect.top;
    const lastCircleCenterY = lastCircleRect.top + lastCircleRadius - containerRect.top;
    const trackTop = (firstCircleCenterY + lastCircleCenterY) / 2;

    const firstCircleCenterX = firstCircleRect.left + firstCircleRadius - containerRect.left;
    const lastCircleCenterX = lastCircleRect.left + lastCircleRadius - containerRect.left;

    const trackStart = firstCircleCenterX + firstCircleRadius;
    const trackEnd = containerRect.width - (lastCircleCenterX - lastCircleRadius);

    progressTrack.style.setProperty('--track-top', `${trackTop}px`);
    progressTrack.style.setProperty('--track-start', `${trackStart}px`);
    progressTrack.style.setProperty('--track-end', `${trackEnd}px`);

    trackReady = true;
  }

  function updateProgressBar(quantity) {
    if (!progressBarElement || thresholds.length === 0) {
      initProgressBar();
      if (!progressBarElement || thresholds.length === 0) return;
    }

    const quantityNum = parseInt(quantity, 10) || 0;

    progressBarElement.dataset.currentQuantity = String(quantityNum);

    if (!progressFill || !progressTrack) return;
    if (!trackReady) updateTrackPosition();

    let fillPercentage = 0;
    const maxThreshold = thresholds[thresholds.length - 1].value;

    if (quantityNum >= maxThreshold) {
      fillPercentage = 100;
    } else if (thresholds.length === 1) {
      const denom = thresholds[0].value;
      if (denom > 0) {
        fillPercentage = Math.min(100, (quantityNum / denom) * 100);
      } else {
        // Degenerate case: threshold is 0, so any non-negative quantity is "maxed".
        fillPercentage = quantityNum >= maxThreshold ? 100 : 0;
      }
    } else {
      let segmentStartIndex = -1;
      let segmentEndIndex = -1;
      let segmentStartThreshold = 0;
      let segmentEndThreshold = maxThreshold;

      if (quantityNum < thresholds[0].value) {
        segmentStartIndex = -1;
        segmentEndIndex = 0;
        segmentStartThreshold = 0;
        segmentEndThreshold = thresholds[0].value;
      } else if (quantityNum >= maxThreshold) {
        segmentStartIndex = thresholds.length - 1;
        segmentEndIndex = thresholds.length;
        segmentStartThreshold = maxThreshold;
        segmentEndThreshold = maxThreshold;
      } else {
        for (let i = 0; i < thresholds.length - 1; i++) {
          if (quantityNum >= thresholds[i].value && quantityNum < thresholds[i + 1].value) {
            segmentStartIndex = i;
            segmentEndIndex = i + 1;
            segmentStartThreshold = thresholds[i].value;
            segmentEndThreshold = thresholds[i + 1].value;
            break;
          }
        }
      }

      if (!progressContainer) return;
      const containerRect = progressContainer.getBoundingClientRect();
      const trackStart = parseFloat(progressTrack.style.getPropertyValue('--track-start')) || 0;
      const trackEnd = parseFloat(progressTrack.style.getPropertyValue('--track-end')) || 0;
      const trackWidth = containerRect.width - trackStart - trackEnd;

      let fillPosition = 0;

      if (segmentStartIndex === -1) {
        const startCircle = thresholds[segmentEndIndex].circle;
        if (startCircle) {
          const startCircleRect = startCircle.getBoundingClientRect();
          const startCircleCenterX = startCircleRect.left + startCircleRect.width / 2 - containerRect.left;
          const progressInSegment = segmentEndThreshold > 0 ? quantityNum / segmentEndThreshold : 0;
          fillPosition = trackStart + (startCircleCenterX - trackStart) * progressInSegment;
        }
      } else if (segmentStartIndex === thresholds.length - 1) {
        fillPosition = containerRect.width - trackEnd;
      } else {
        const startCircle = thresholds[segmentStartIndex].circle;
        const endCircle = thresholds[segmentEndIndex].circle;

        if (startCircle && endCircle) {
          const startCircleRect = startCircle.getBoundingClientRect();
          const endCircleRect = endCircle.getBoundingClientRect();
          const startCircleCenterX = startCircleRect.left + startCircleRect.width / 2 - containerRect.left;
          const endCircleCenterX = endCircleRect.left + endCircleRect.width / 2 - containerRect.left;

          const segmentSize = segmentEndThreshold - segmentStartThreshold;
          const quantityInSegment = quantityNum - segmentStartThreshold;
          const progressInSegment = segmentSize > 0 ? quantityInSegment / segmentSize : 1;

          fillPosition = startCircleCenterX + (endCircleCenterX - startCircleCenterX) * progressInSegment;
        }
      }

      const fillWidth = fillPosition - trackStart;
      fillPercentage = trackWidth > 0 ? Math.max(0, Math.min(100, (fillWidth / trackWidth) * 100)) : 0;
    }

    progressFill.style.setProperty('--progress', `${fillPercentage}%`);

    for (const threshold of thresholds) {
      const isReached = quantityNum >= threshold.value;
      const reachedAttr = isReached ? 'true' : 'false';
      threshold.element.setAttribute('data-reached', reachedAttr);
      if (threshold.circle) threshold.circle.setAttribute('data-reached', reachedAttr);
    }
  }

  window.updateProgressBar = updateProgressBar;

  let resizeTimeout;
  function onResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      updateTrackPosition();
      if (progressBarElement && progressBarElement.dataset.currentQuantity !== undefined) {
        const currentQuantity = parseInt(progressBarElement.dataset.currentQuantity, 10) || 0;
        window.updateProgressBar(currentQuantity);
      }
    }, 100);
  }

  function boot() {
    initProgressBar();
    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
